import {
  createContext,
  type Dispatch,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useImages } from "./DealerDataProviderContext.tsx";

type EventHandler = (event?: Parameters<MouseEventHandler>[0]) => void;

type GalleryState = {
  fullScreen: boolean;
  setFullScreen: Dispatch<SetStateAction<boolean>>;
  currentImageIndex: number;
  setImageIndex: (value: number) => void;
  showNextImage: EventHandler;
  showPreviousImage: EventHandler;
  isScrolling: RefObject<boolean>;
};

const initialState: GalleryState = {
  fullScreen: false,
  setFullScreen: () => {},
  currentImageIndex: 0,
  setImageIndex: () => {},
  showNextImage: () => {},
  showPreviousImage: () => {},
  isScrolling: { current: false },
};

const GalleryStateContext = createContext(initialState);

export const useGalleryState = () => useContext(GalleryStateContext);

export const GalleryStateProvider = ({ children }: { children: ReactNode }) => {
  const [currentImageIndex, setImageIndex] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);
  const images = useImages();
  const isScrolling = useRef(false);

  const scrollToIndexInstant = (index: number) => {
    document.getElementById(`gallery-image-${index}`)?.scrollIntoView({ block: "nearest", behavior: "instant" });
  };

  const showNextImage: EventHandler = (event) => {
    event?.stopPropagation();
    const len = images.length;
    if (len === 0) return;
    const nextIndex = (currentImageIndex + 1) % len;
    isScrolling.current = true;
    setImageIndex(nextIndex);
    scrollToIndexInstant(nextIndex);
  };

  const showPreviousImage: EventHandler = (event) => {
    event?.stopPropagation();
    const len = images.length;
    if (len === 0) return;
    const prevIndex = (currentImageIndex - 1 + len) % len;
    isScrolling.current = true;
    setImageIndex(prevIndex);
    scrollToIndexInstant(prevIndex);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!fullScreen) {
      return;
    }
    if (event.key === "Escape") {
      setFullScreen(false);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      showNextImage();
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPreviousImage();
      return;
    }
    if (event.key === " ") {
      event.preventDefault();
      showNextImage();
    }
  };

  const keydownHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keydownHandlerRef.current = onKeyDown;

  useEffect(() => {
    if (!fullScreen) {
      return;
    }
    const listener = (e: KeyboardEvent) => keydownHandlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, [fullScreen]);

  return (
    <GalleryStateContext
      value={{
        fullScreen,
        setFullScreen,
        currentImageIndex,
        setImageIndex,
        showNextImage,
        showPreviousImage,
        isScrolling,
      }}
    >
      {children}
    </GalleryStateContext>
  );
};
